import {
  accessibilityPreferencesSchema,
  reducedMotionPatchSchema,
  singleKeyShortcutsPatchSchema,
  type AccessibilityPreferences,
} from '@molt/shared';
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { get, patch } from './api';
import { queryKeys } from './query-keys';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export function getAccessibilityPreferences(): Promise<AccessibilityPreferences> {
  return get('/me/accessibility-preferences', accessibilityPreferencesSchema);
}

export function setSingleKeyShortcutsDisabled(
  disabled: boolean,
): Promise<AccessibilityPreferences> {
  return patch(
    '/me/accessibility-preferences/single-key-shortcuts',
    accessibilityPreferencesSchema,
    singleKeyShortcutsPatchSchema.parse({ singleKeyShortcutsDisabled: disabled }),
  );
}

export function setReducedMotionOverride(
  enabled: boolean,
): Promise<AccessibilityPreferences> {
  return patch(
    '/me/accessibility-preferences/reduced-motion',
    accessibilityPreferencesSchema,
    reducedMotionPatchSchema.parse({ reducedMotionOverride: enabled }),
  );
}

export function accessibilityPreferencesQueryOptions(): UseQueryOptions<
  AccessibilityPreferences,
  unknown
> {
  return {
    queryKey: queryKeys.meAccessibility(),
    queryFn: getAccessibilityPreferences,
  };
}

export function useSetSingleKeyShortcutsDisabled(): UseMutationResult<
  AccessibilityPreferences,
  unknown,
  boolean
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setSingleKeyShortcutsDisabled,
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKeys.meAccessibility(), preferences);
    },
  });
}

export function useSetReducedMotionOverride(): UseMutationResult<
  AccessibilityPreferences,
  unknown,
  boolean
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setReducedMotionOverride,
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKeys.meAccessibility(), preferences);
    },
  });
}

export function useReducedMotionPreference(
  preferences: AccessibilityPreferences | null | undefined,
): boolean {
  const [systemReducedMotion, setSystemReducedMotion] = useState(readSystemReducedMotion);

  useEffect(() => {
    if (typeof window === 'undefined' || window.matchMedia === undefined) return;
    const media = window.matchMedia(REDUCED_MOTION_QUERY);
    const sync = () => setSystemReducedMotion(media.matches);

    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return preferences?.reducedMotionOverride === true || systemReducedMotion;
}

function readSystemReducedMotion(): boolean {
  if (typeof window === 'undefined' || window.matchMedia === undefined) return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}
